<template>
  <BaseDialog :open="open" title="Klasse bearbeiten" size="lg" @close="closeClassMenu">
    <div class="top">
      <label class="field">
        <span class="field-label">Name</span>
        <input class="input" v-model.trim="className" maxlength="40" @change="submitClassName" v-on:keyup.enter="submitClassName()">
      </label>
      <fieldset class="hues">
        <legend class="field-label">Farbe der Klasse</legend>
        <label v-for="hue in hues" :key="hue.key" class="hue-option" :data-hue="hue.key">
          <input type="radio" class="visually-hidden" name="class-hue" :value="hue.key" :checked="config.hue === hue.key" @change="changeHue(hue.key)">
          <span class="swatch"></span> {{ hue.label }}
        </label>
      </fieldset>
    </div>

    <section class="students">
      <h3 class="leaf-title">{{ studentsLabel }}</h3>
      <form class="field" @submit.prevent="addStudent()">
        <label class="field-label" for="new-student">Neue:r Schüler:in</label>
        <div class="inline-form">
          <input id="new-student" class="input" type="text" placeholder="Vorname Nachname" autocomplete="off" v-model="studentName" @paste="pasteNames">
          <button type="submit" class="btn btn-primary" :disabled="studentName.trim().length === 0">
            <Plus aria-hidden="true" /> Hinzufügen
          </button>
        </div>
        <span class="field-hint">Mehrere auf einmal: Namen mit Komma trennen, oder eine Liste mit einem Namen pro Zeile einfügen.</span>
      </form>

      <p v-if="config.students.length === 0" class="hint">Noch niemand in dieser Klasse.</p>
      <div v-else class="click-mode">
        <span class="field-label" id="click-mode-label">Ein Klick auf einen Namen ändert die</span>
        <div class="segmented" role="group" aria-labelledby="click-mode-label">
          <button type="button" :aria-pressed="clickMode === 'separate' ? 'true' : 'false'" @click="clickMode = 'separate'">Trenngruppe</button>
          <button type="button" :aria-pressed="clickMode === 'together' ? 'true' : 'false'" @click="clickMode = 'together'">Partnergruppe</button>
        </div>
      </div>
      <ul v-if="config.students.length > 0" class="student-list">
        <li v-for="student in config.students" :key="student.id" class="student" :class="`blocking-group-${student.blockingGroup}`">
          <button type="button" class="student-name" :title="groupTitle(student)" @click="changeGroup(student.id)">
            {{ student.name }}
            <span v-if="student.blockingGroup" class="group-badge">
              <span class="visually-hidden">Trenngruppe</span> {{ letters[student.blockingGroup] }}
            </span>
            <span v-if="student.partnerGroup" class="partner-badge">
              <span class="visually-hidden">Partnergruppe</span> <Link2 aria-hidden="true" /> {{ student.partnerGroup }}
            </span>
          </button>
          <button type="button" class="student-remove" :aria-label="`${student.name} entfernen`" @click="removeStudent(student.id)">
            <X aria-hidden="true" />
          </button>
        </li>
      </ul>
    </section>

    <div class="form-note groups-note">
      <div class="groups-line">
        <p><strong>Trenngruppen</strong> A, B und C: Wer in derselben Gruppe ist, sitzt nicht nebeneinander und kommt nicht ins gleiche Team.</p>
        <p class="group-samples" aria-hidden="true">
          <span class="chip blocking-group-1">A</span>
          <span class="chip blocking-group-2">B</span>
          <span class="chip blocking-group-3">C</span>
        </p>
      </div>
      <div class="groups-line">
        <p><strong>Partnergruppen</strong> 1 bis {{ partnerGroups }}: Wer in derselben Gruppe ist, sitzt nebeneinander und kommt ins gleiche Team.</p>
        <p class="group-samples" aria-hidden="true">
          <span class="partner-badge" v-for="number in partnerGroups" :key="number"><Link2 /> {{ number }}</span>
        </p>
      </div>
    </div>
  </BaseDialog>
</template>

  <script>
  import { Link2, Plus, X } from '@lucide/vue';
  import BaseDialog from './ui/BaseDialog.vue';
  import { CLASS_HUES, GROUP_LETTERS, PARTNER_GROUPS, createStudent, plural } from '../config.js';

  export default {
    name: 'Klasse',
    data() {
      return  {
        className: "",
        studentName: "",
        hues: CLASS_HUES,
        letters: GROUP_LETTERS,
        partnerGroups: PARTNER_GROUPS,
        // What a click on a name changes: 'separate' or 'together'
        clickMode: 'separate',
      }
    },
    created() {
        this.className = this.config.name
    },
    props: {
      config: Object,
      open: Boolean,
    },
    emits: ['updateStudents', 'changeClassName', 'changeClassHue', 'closeClassMenu'],
    components: {
      BaseDialog, Link2, Plus, X,
    },
    computed: {
      studentsLabel() {
        return plural(this.config.students.length, 'Schüler:in', 'Schüler:innen')
      },
    },
    watch: {
        'config.id': function() {
            this.className = this.config.name
            this.studentName = "";
        },
        open: function() {
            this.className = this.config.name
        }
    },
    methods: {
        // Steps a student to the next separation or partner group, and after the last one back to none.
        changeGroup: function(id) {
          const next = student => this.clickMode === 'together'
            ? { ...student, partnerGroup: (student.partnerGroup + 1) % (this.partnerGroups + 1) }
            : { ...student, blockingGroup: (student.blockingGroup + 1) % 4 }
          this.$emit("updateStudents", this.config.students.map(s => s.id === id ? next(s) : s))
        },
        groupTitle: function(student) {
          const groups = []
          if (student.blockingGroup) groups.push(`Trenngruppe ${this.letters[student.blockingGroup]}`)
          if (student.partnerGroup) groups.push(`Partnergruppe ${student.partnerGroup}`)
          const action = this.clickMode === 'together' ? 'Klicken, um die Partnergruppe zu wechseln' : 'Klicken, um die Trenngruppe zu wechseln'
          return groups.length > 0 ? `${groups.join(', ')}. ${action}` : action
        },
        addNames: function(text) {
            const known = this.config.students.map(s => s.name)
            const names = text.split(/[\n\r,;\t]+/).map(n => n.trim().replace(/\s+/g, ' ')).filter(n => n.length > 0)
            if (names.length === 0) return
            this.$emit("updateStudents", [...this.config.students, ...names.filter(n => !known.includes(n)).map(n => createStudent(n))])
        },
        addStudent: function() {
            this.addNames(this.studentName)
            this.studentName = "";
        },
        // A pasted list (one name per line) goes straight in.
        pasteNames: function(event) {
            const text = event.clipboardData ? event.clipboardData.getData('text') : ''
            if (!/[\n\r]/.test(text.trim())) return
            event.preventDefault()
            this.addNames(text)
        },
        removeStudent: function(id) {
            this.$emit("updateStudents", this.config.students.filter(s => s.id !== id))
        },
        submitClassName: function() {
            if (this.className.length === 0) {
              this.className = this.config.name
              return
            }
            this.$emit("changeClassName", this.className);
        },
        changeHue: function(hue) {
            this.$emit("changeClassHue", hue);
        },
        closeClassMenu: function() {
          this.$emit("closeClassMenu")
        },
    }
  }
  </script>

  <style scoped>
  .top {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) auto;
    gap: var(--s-4) var(--s-5);
    align-items: start;
  }

  @media (max-width: 799px) {
    .top {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .hues {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    margin: 0;
    padding: 0;
    border: 0;
    min-width: 0;
  }

  .hues legend {
    padding: 0;
    margin-bottom: var(--s-1);
  }

  .hue-option {
    display: inline-flex;
    align-items: center;
    gap: var(--s-2);
    min-height: var(--tap);
    padding: 0 var(--s-3);
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--paper);
    box-shadow: var(--shadow-sm);
    font-size: var(--t-sm);
    font-weight: 700;
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }

  .hue-option:hover {
    background: var(--hue-wash);
  }

  /* The chosen colour fills its own card; its swatch turns ink. */
  .hue-option:has(:checked) {
    background: var(--hue);
  }

  .hue-option:has(:checked) .swatch {
    background: var(--ink);
    box-shadow: 0 0 0 2px var(--paper) inset;
  }

  .hue-option:has(:focus-visible) {
    outline: 3px solid var(--ink);
    outline-offset: 2px;
  }

  .students {
    display: grid;
    gap: var(--s-3);
    padding-top: var(--s-4);
    border-top: var(--line);
  }

  .student-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .student {
    display: inline-flex;
    border: var(--line);
    border-radius: var(--r-pill);
    background: var(--paper);
    overflow: hidden;
  }

  .blocking-group-1 { background: var(--group-a); }
  .blocking-group-2 { background: var(--group-b); }
  .blocking-group-3 { background: var(--group-c); }

  .student button {
    display: inline-flex;
    align-items: center;
    gap: var(--s-2);
    min-height: 36px;
    border: 0;
    background: transparent;
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }

  .student button:hover {
    background: rgb(21 22 26 / 0.08);
  }

  .student button:focus-visible {
    outline-offset: -4px;
    border-radius: var(--r-pill);
  }

  .student-name {
    padding: 0 var(--s-2) 0 var(--s-3);
    font-size: var(--t-sm);
    font-weight: 700;
  }

  .group-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--ink);
    color: var(--paper);
    font-size: var(--t-xs);
    font-weight: 800;
  }

  /* Partner groups are numbers on paper, separation groups letters on ink: two shapes, never only a colour. */
  .partner-badge {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 20px;
    padding: 0 6px 0 4px;
    border: var(--line);
    border-radius: 6px;
    background: var(--paper);
    font-size: var(--t-xs);
    font-weight: 800;
  }

  .partner-badge svg {
    width: 12px;
    height: 12px;
    stroke-width: 3;
  }

  .click-mode {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-2) var(--s-3);
  }

  .student-remove {
    padding: 0 10px 0 6px;
  }

  .student-remove svg {
    width: 14px;
    height: 14px;
    stroke-width: 3;
  }

  .groups-note {
    display: grid;
    gap: var(--s-2);
    font-weight: 600;
  }

  .groups-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-2) var(--s-4);
  }

  .groups-line p:first-child {
    flex: 1 1 320px;
  }

  .groups-note strong {
    font-weight: 800;
  }

  .group-samples {
    display: flex;
    gap: var(--s-1);
  }
  </style>
